import React, { useContext, createContext, useState, useEffect } from 'react';
import { useAnimation, motion } from 'framer-motion';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation. All rights reserved.
Licensed under the Apache License, Version 2.0 (the "License"); you may not use
this file except in compliance with the License. You may obtain a copy of the
License at http://www.apache.org/licenses/LICENSE-2.0

THIS CODE IS PROVIDED ON AN *AS IS* BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED
WARRANTIES OR CONDITIONS OF TITLE, FITNESS FOR A PARTICULAR PURPOSE,
MERCHANTABLITY OR NON-INFRINGEMENT.

See the Apache Version 2.0 License for specific language governing permissions
and limitations under the License.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

function __generator(thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
}

var defaultConfig = {
  openByDefault: false,
  footer: 'Signum - created by Nightborn.be',
  avatarIcon: '',
  mainColor: 'linear-gradient(158.5deg, #270E41 -32.82%, #112441 120.45%)',
  secondaryColor: 'linear-gradient(137.56deg, #2DDC9F 1.03%, #1DC6AF 99.9%)',
  sendButtonColor: '#112441',
  finalButtonColor: "linear-gradient(163.56deg, #7A22AF -5.81%, #F40078 113.99%)",
  emailPlaceholder: 'Leave us your e-mail address',
  messagePlaceholder: 'Write your message',
  finalTitle: 'Thank you.',
  finalSubTitle: "We'll be in touch!",
  finalButtonText: "Continue",
  handleFinalButtonClicked: function handleFinalButtonClicked() {},
  handleSendClicked: function handleSendClicked(information) {
    return console.log(information);
  }
};
var defaultOption = {
  title: 'Hello there 👋',
  subTitle: "We're glad to see you on our website.",
  message: 'How can I help you?',
  name: 'Hello there 👋'
};

var ChatContext = createContext({
  isOpen: false,
  config: defaultConfig,
  openChat: function openChat() {},
  closeChat: function closeChat() {},
  defaultOption: defaultOption
});

function useChat() {
  return useContext(ChatContext);
}

function ChatBody() {
  // Attributes
  var _a = useChat(),
      isOpen = _a.isOpen,
      closeChat = _a.closeChat,
      config = _a.config,
      defaultOption = _a.defaultOption;

  var apparitionAnimation = useAnimation();
  var apparitionHeaderAnimation = useAnimation();
  var apparitionBodyAnimation = useAnimation();
  var apparitionFooterAnimation = useAnimation();

  var _b = useState(defaultOption),
      option = _b[0],
      setOption = _b[1];

  var _c = useState([]),
      steps = _c[0],
      setSteps = _c[1];

  var _d = useState(''),
      email = _d[0],
      setEmail = _d[1];

  var _e = useState(''),
      message = _e[0],
      setMessage = _e[1];

  var _f = useState(false),
      isDone = _f[0],
      setIsDone = _f[1]; // Effects


  useEffect(function () {
    if (!isOpen) {
      outSequence();
    } else {
      inSequence();
    }
  }, [isOpen]); // Methods

  function inSequence() {
    return __awaiter(this, void 0, void 0, function () {
      return __generator(this, function (_a) {
        apparitionAnimation.start({
          scaleY: 0,
          opacity: 0,
          x: 0,
          transition: {
            duration: 0
          }
        });
        apparitionAnimation.start({
          opacity: 1,
          scaleY: 1
        });
        apparitionHeaderAnimation.start({
          opacity: 1,
          y: 0,
          transition: {
            delay: 0.2
          }
        });
        apparitionBodyAnimation.start({
          y: 0,
          opacity: 1,
          transition: {
            delay: 0.2
          }
        });
        apparitionFooterAnimation.start({
          y: 0,
          opacity: 1,
          scale: 1,
          transition: {
            delay: 0.2,
            ease: "linear"
          }
        });
        return [2
        /*return*/
        ];
      });
    });
  }

  function outSequence() {
    return __awaiter(this, void 0, void 0, function () {
      return __generator(this, function (_a) {
        switch (_a.label) {
          case 0:
            return [4
            /*yield*/
            , apparitionAnimation.start({
              scaleY: 1,
              opacity: 0,
              x: 450,
              transition: {
                duration: 0.5
              }
            })];

          case 1:
            _a.sent();

            apparitionHeaderAnimation.start({
              opacity: 0,
              y: 10,
              transition: {
                duration: 0.3
              }
            });
            apparitionBodyAnimation.start({
              y: -10,
              opacity: 0,
              transition: {
                duration: 0.3
              }
            });
            apparitionFooterAnimation.start({
              y: 10,
              opacity: 0,
              transition: {
                duration: 0.3,
                ease: "linear"
              }
            });
            return [2
            /*return*/
            ];
        }
      });
    });
  }

  function handleOptionClicked(option) {
    setSteps(function (steps) {
      steps.push(option);
      return steps;
    });
    setOption(option);
  }

  function handleBackClicked() {
    // Resets the current values
    setMessage('');
    setEmail(''); // Sets the current option to the previous one

    if (steps.length >= 2) {
      setOption(steps[steps.length - 2]);
    } else {
      setOption(defaultOption);
    } // Removes the previous step


    setSteps(function (steps) {
      steps.pop();
      return steps;
    });
  }

  function handleSendClicked() {
    return __awaiter(this, void 0, void 0, function () {
      return __generator(this, function (_a) {
        switch (_a.label) {
          case 0:
            if (!isFormFilledIn()) return [3
            /*break*/
            , 2];
            return [4
            /*yield*/
            , config.handleSendClicked({
              email: email,
              message: message,
              steps: steps,
              option: option
            })];

          case 1:
            _a.sent();

            setIsDone(true);
            _a.label = 2;

          case 2:
            return [2
            /*return*/
            ];
        }
      });
    });
  }

  function handleFinishClicked() {
    return __awaiter(this, void 0, void 0, function () {
      return __generator(this, function (_a) {
        switch (_a.label) {
          case 0:
            return [4
            /*yield*/
            , outSequence()];

          case 1:
            _a.sent();

            reset();

            if (config.handleFinalButtonClicked) {
              config.handleFinalButtonClicked();
            }

            return [2
            /*return*/
            ];
        }
      });
    });
  }
  /**
   * Returns if the message and e-mail are filled in
   */


  function isFormFilledIn() {
    if (message && email) {
      return true;
    }

    return false;
  }
  /**
   * Resets the entire state of the chat
   */


  function reset() {
    setOption(defaultOption);
    setSteps([]);
    setIsDone(false);
    setEmail('');
    setMessage('');
    closeChat();
  } // Render


  if (isDone) {
    return /*#__PURE__*/React.createElement(motion.div, {
      initial: {
        scaleY: 0,
        opacity: 0
      },
      animate: apparitionAnimation,
      className: "chat-body-small-container"
    }, /*#__PURE__*/React.createElement("div", {
      className: "chat-body-done-title"
    }, config.finalTitle), /*#__PURE__*/React.createElement("div", {
      className: "chat-body-done-sub-title"
    }, config.finalSubTitle), /*#__PURE__*/React.createElement(motion.div, {
      onClick: handleFinishClicked,
      whileHover: {
        scale: 1.05
      },
      whileTap: {
        scale: 0.9
      },
      className: "chat-body-done-button",
      style: {
        background: config.finalButtonColor
      }
    }, config.finalButtonText));
  }

  return /*#__PURE__*/React.createElement(motion.div, {
    initial: {
      scaleY: 0,
      opacity: 0,
      x: 500
    },
    animate: apparitionAnimation,
    className: "chat-body-container"
  }, /*#__PURE__*/React.createElement("div", {
    className: "chat-body-header",
    style: {
      background: config.mainColor
    }
  }, /*#__PURE__*/React.createElement(motion.div, {
    initial: {
      opacity: 0,
      y: 10
    },
    animate: apparitionHeaderAnimation
  }, option.subTitle ? /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
    className: "chat-body-header-title"
  }, option.title), /*#__PURE__*/React.createElement("div", {
    className: "chat-body-header-body"
  }, option.subTitle)) : /*#__PURE__*/React.createElement("div", {
    className: "chat-body-header-small"
  }, /*#__PURE__*/React.createElement(motion.div, {
    whileHover: {
      x: -2
    },
    whileTap: {
      scale: 0.9
    },
    onClick: handleBackClicked,
    className: "chat-body-header-small-image"
  }, /*#__PURE__*/React.createElement("svg", {
    width: "20",
    height: "20",
    viewBox: "0 0 20 20",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M4.76036 10.388C4.76712 10.3955 4.77411 10.4028 4.78133 10.4101L9.88097 15.5188C10.0316 15.6697 10.0314 15.914 9.88049 16.0646C9.72963 16.2152 9.48525 16.215 9.33465 16.0641L4.23503 10.9554C4.0031 10.7231 3.87334 10.4275 3.84573 10.1242C3.83291 10.0858 3.82597 10.0447 3.82597 10.002C3.82597 9.95954 3.83282 9.91869 3.84548 9.88048C3.8726 9.57589 4.00275 9.27875 4.23591 9.04566L9.34254 3.93958C9.49327 3.78886 9.73765 3.78887 9.88837 3.93961C10.0391 4.09035 10.0391 4.33473 9.88834 4.48545L4.78169 9.59155C4.7737 9.59954 4.766 9.6077 4.75857 9.61602L17.3347 9.61602C17.5479 9.61602 17.7207 9.78883 17.7207 10.002C17.7207 10.2152 17.5479 10.388 17.3347 10.388L4.76036 10.388Z",
    fill: "white",
    stroke: "white"
  }))), /*#__PURE__*/React.createElement("div", {
    className: "chat-body-header-small-title"
  }, option.title)))), /*#__PURE__*/React.createElement(motion.div, {
    initial: {
      opacity: 0,
      y: -10
    },
    animate: apparitionBodyAnimation,
    className: "chat-body-content"
  }, /*#__PURE__*/React.createElement("div", {
    className: "chat-body-content-message"
  }, /*#__PURE__*/React.createElement("img", {
    src: config.avatarIcon,
    className: "chat-body-content-message-image"
  }), /*#__PURE__*/React.createElement("div", {
    className: "chat-body-content-message-body"
  }, option.message)), /*#__PURE__*/React.createElement("div", {
    className: "chat-body-content-options"
  }, option.options && option.options.map(function (option) {
    return /*#__PURE__*/React.createElement(motion.div, {
      whileHover: {
        scale: 1.1
      },
      whileTap: {
        scale: 0.9
      },
      onClick: function onClick() {
        return handleOptionClicked(option);
      },
      className: "chat-body-content-option",
      style: {
        background: config.mainColor
      }
    }, option.name);
  }))), option.options === undefined ? /*#__PURE__*/React.createElement(motion.div, {
    animate: apparitionFooterAnimation,
    className: "chat-body-messenger"
  }, /*#__PURE__*/React.createElement("div", {
    className: "chat-body-messenger-body"
  }, /*#__PURE__*/React.createElement("input", {
    autoFocus: true,
    value: email,
    onChange: function onChange(event) {
      return setEmail(event.target.value);
    },
    placeholder: config.emailPlaceholder
  }), /*#__PURE__*/React.createElement("textarea", {
    value: message,
    onChange: function onChange(event) {
      return setMessage(event.target.value);
    },
    placeholder: config.messagePlaceholder
  })), /*#__PURE__*/React.createElement("div", {
    className: "chat-body-messenger-footer"
  }, /*#__PURE__*/React.createElement(motion.div, {
    whileHover: {
      x: isFormFilledIn() ? 2 : 0
    },
    whileTap: {
      scale: 0.9
    },
    onClick: handleSendClicked,
    className: "chat-body-messenger-footer-image",
    style: {
      cursor: isFormFilledIn() ? 'pointer' : 'not-allowed'
    }
  }, /*#__PURE__*/React.createElement("svg", {
    width: "24",
    height: "24",
    viewBox: "0 0 24 24",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M0.0275001 22.8866L2.05466 14.1079C2.13468 13.7344 2.45476 13.4409 2.85485 13.3875L14.1909 12.2135C14.511 12.1868 14.511 11.7065 14.1909 11.6531L2.85485 10.5591C2.45476 10.5324 2.13468 10.2389 2.05466 9.86538L0.0275001 1.11344C-0.159212 0.366318 0.640983 -0.247385 1.33448 0.0994905L23.4999 11.1995C24.1667 11.5464 24.1667 12.507 23.4999 12.8538L1.33448 23.9005C0.640983 24.2474 -0.159212 23.6337 0.0275001 22.8866Z",
    fill: isFormFilledIn() ? config.sendButtonColor : '#C4C4C4'
  }))))) : /*#__PURE__*/React.createElement(motion.div, {
    animate: apparitionFooterAnimation,
    className: "chat-body-footer"
  }, config.footer));
}

const img = "data:image/svg+xml,%3csvg width='29' height='29' viewBox='0 0 29 29' fill='none' xmlns='http://www.w3.org/2000/svg'%3e%3cg clip-path='url(%23clip0)'%3e%3cpath d='M14.8631 0.617126C17.6018 0.617126 20.8277 0.749119 24.5418 1.0129C26.7497 1.16971 28.5166 2.90731 28.7103 5.1123C28.8901 7.15878 28.9799 9.30723 28.9799 11.5577C28.9799 13.8171 28.8894 15.9737 28.7082 18.0275C28.514 20.2286 26.7521 21.9639 24.5483 22.1246C22.6213 22.2651 20.6943 22.3634 18.7672 22.4194L16.5423 26.7925C16.357 27.1566 16.0611 27.4526 15.697 27.6378C14.7466 28.1213 13.5842 27.7429 13.1007 26.7925L10.8728 22.4135C8.96252 22.3556 7.03806 22.2571 5.09938 22.1181C2.89324 21.9598 1.12832 20.2234 0.934181 18.0201C0.753412 15.9685 0.663086 13.8144 0.663086 11.5577C0.663086 9.30814 0.752844 7.1605 0.932475 5.11477C1.12617 2.90884 2.89436 1.17078 5.10329 1.01503C8.8642 0.749829 12.1171 0.617126 14.8631 0.617126ZM14.1779 14.7525H9.67299C8.8149 14.7525 8.8149 16.0396 9.67299 16.0396H14.1779C15.036 16.0396 15.036 14.7525 14.1779 14.7525ZM19.97 10.8911H9.67299C8.8149 10.8911 8.8149 12.1782 9.67299 12.1782H19.97C20.8281 12.1782 20.8281 10.8911 19.97 10.8911ZM19.97 7.02969H9.67299C8.8149 7.02969 8.8149 8.31681 9.67299 8.31681H19.97C20.8281 8.31681 20.8281 7.02969 19.97 7.02969Z' fill='white'/%3e%3c/g%3e%3cdefs%3e%3cclipPath id='clip0'%3e%3crect x='0.663086' y='0.594055' width='28.3168' height='27.6733' fill='white'/%3e%3c/clipPath%3e%3c/defs%3e%3c/svg%3e";

const img$1 = "data:image/svg+xml,%3csvg width='22' height='22' viewBox='0 0 22 22' fill='none' xmlns='http://www.w3.org/2000/svg'%3e%3cpath d='M1.05628 3.44262L8.61366 11L1.05628 18.5574C0.397897 19.2158 0.397897 20.2853 1.05628 20.9437L1.36565 20.6344L1.05628 20.9437C1.71467 21.6021 2.78423 21.6021 3.44262 20.9437L11 13.3863L18.5574 20.9437C19.2158 21.6021 20.2853 21.6021 20.9437 20.9437C21.6021 20.2853 21.6021 19.2158 20.9437 18.5574L13.3863 11L20.9437 3.44262C21.6021 2.78423 21.6021 1.71467 20.9437 1.05628L20.6344 1.36565L20.9437 1.05628C20.2853 0.397897 19.2158 0.397897 18.5574 1.05628L11 8.61366L3.44262 1.05628C2.78423 0.397897 1.71467 0.397897 1.05628 1.05628C0.397897 1.71467 0.397897 2.78423 1.05628 3.44262Z' fill='white' stroke='white' stroke-width='0.875013'/%3e%3c/svg%3e";

function ChatIcon() {
  // Attributes
  var _a = useChat(),
      isOpen = _a.isOpen,
      closeChat = _a.closeChat,
      openChat = _a.openChat,
      config = _a.config; // Methods


  function getImage() {
    if (isOpen) {
      return img$1;
    }

    return img;
  }

  function getImageClass() {
    if (isOpen) {
      return "chat-button-image-close";
    }

    return "chat-button-image";
  } // Render


  return /*#__PURE__*/React.createElement(motion.div, {
    initial: {
      y: 100
    },
    animate: {
      y: 0,
      transition: {
        delay: 1.5
      }
    },
    onClick: function onClick() {
      return isOpen ? closeChat() : openChat();
    },
    whileHover: {
      scale: 1.1
    },
    whileTap: {
      scale: 0.9
    },
    className: "chat-button",
    style: {
      background: config.secondaryColor
    }
  }, /*#__PURE__*/React.createElement("img", {
    src: getImage(),
    className: getImageClass()
  }));
}

function ChatContextProvider(props) {
  // Attributes
  var option = props.option,
      config = props.config;

  var _a = useState(config.openByDefault),
      isOpen = _a[0],
      setIsOpen = _a[1]; // Methods


  function computeValue() {
    return {
      isOpen: isOpen,
      config: __assign(__assign({}, defaultConfig), config),
      defaultOption: __assign(__assign({}, defaultOption), option),
      openChat: function openChat() {
        return setIsOpen(true);
      },
      closeChat: function closeChat() {
        return setIsOpen(false);
      }
    };
  } // Render


  return /*#__PURE__*/React.createElement(ChatContext.Provider, {
    value: computeValue()
  }, props.children);
}

function Chat(props) {
  return /*#__PURE__*/React.createElement(ChatContextProvider, props, /*#__PURE__*/React.createElement(ChatIcon, null), /*#__PURE__*/React.createElement(ChatBody, null));
}

export default Chat;
